import { useCallback, useState } from 'react';
import { Image, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import * as Location from 'expo-location';
import * as MediaLibrary from 'expo-media-library';
import { useAuth } from '../context/AuthContext';
import {
  getSubmission,
  checkin,
  submitSubmission,
  uploadPhoto,
  disputeSubmission,
  cancelSubmission,
} from '../api/submissions';
import { Submission } from '../types';
import {
  Button,
  Caption,
  Card,
  Checkbox,
  ConfirmDialog,
  ErrorText,
  FormInput,
  Label,
  LoadingState,
  ScreenContainer,
} from '../components/ui';
import { colors, fonts } from '../theme';
import { getCurrentPositionWithTimeout } from '../utils/location';
import { ApiError } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { CameraCapture } from '../components/CameraCapture';
import { resolvePhotoUrl } from '../config';
import { openDirections, openExternalUrl, saveImageOnWeb } from '../utils/openUrl';
import { ShopRatingForm } from '../components/ShopRatingForm';

export default function ReviewerSubmissionFlowScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { token } = useAuth();
  const router = useRouter();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [googleReviewUrl, setGoogleReviewUrl] = useState('');
  const [reviewerNote, setReviewerNote] = useState('');
  const [referredCustomerName, setReferredCustomerName] = useState('');
  const [referredCustomerPhone, setReferredCustomerPhone] = useState('');
  const [confirmedReviewPosted, setConfirmedReviewPosted] = useState(false);
  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputing, setDisputing] = useState(false);
  const [confirmCancelVisible, setConfirmCancelVisible] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || !id) return;
    try {
      setLoadError(null);
      setSubmission(await getSubmission(token, id));
    } catch (e) {
      // Without this, a failed fetch here left the screen stuck on
      // <LoadingState /> forever with zero feedback — the same silent-hang
      // bug class already found and fixed once today in CameraCapture.
      setLoadError(e instanceof ApiError ? e.message : 'Không tải được nhiệm vụ, vui lòng thử lại.');
    }
  }, [token, id]);

  useFocusEffect(
    useCallback(() => {
      load();
    }, [load]),
  );

  const onCheckin = async () => {
    if (!token || !submission) return;
    setError(null);
    setBusy(true);
    try {
      const { status } = await Location.requestForegroundPermissionsAsync();
      if (status !== 'granted') {
        setError('Cần được cấp quyền truy cập vị trí để check-in');
        return;
      }
      const pos = await getCurrentPositionWithTimeout({ accuracy: Location.Accuracy.High });
      await checkin(token, submission.id, pos.coords.latitude, pos.coords.longitude);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Check-in thất bại');
    } finally {
      setBusy(false);
    }
  };

  const onPhotoTaken = async (uri: string, file?: File) => {
    if (!token || !submission) return;
    setShowCamera(false);
    setBusy(true);
    setError(null);
    try {
      // Best-effort: save every captured photo to the phone's gallery too —
      // useful for Google-review tasks (attach the same photo to the real
      // review) but also just generally convenient. Don't block the upload
      // if saving fails for any reason. Web has no MediaLibrary
      // implementation AND a script-triggered download reliably gets
      // silently blocked by iOS Safari outside a direct tap (this ran
      // inside an async camera callback, not a raw click handler) — so on
      // web we skip auto-save entirely and instead let the reviewer tap
      // each uploaded thumbnail to open/save it manually (see the photo
      // grid below).
      if (Platform.OS !== 'web') {
        try {
          const { status } = await MediaLibrary.requestPermissionsAsync(true);
          if (status === 'granted') {
            await MediaLibrary.createAssetAsync(uri);
          }
        } catch (e) {
          // Non-fatal — saving to gallery is a convenience, not a
          // requirement, but surfaced so it doesn't look like the whole
          // capture silently failed.
          console.warn('Không lưu được ảnh vào thư viện:', e);
        }
      }
      await uploadPhoto(token, submission.id, uri, file);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Tải ảnh lên thất bại');
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async (confirmed: boolean) => {
    if (!token || !submission) return;
    setError(null);
    if (!confirmed) {
      setError('Vui lòng xác nhận đã đăng đánh giá trước khi nộp bài');
      return;
    }
    if (submission.task?.taskType === 'CUSTOMER_REFERRAL' && (!referredCustomerName.trim() || !referredCustomerPhone.trim())) {
      setError('Vui lòng nhập tên và số điện thoại của khách được giới thiệu');
      return;
    }
    setBusy(true);
    try {
      await submitSubmission(
        token,
        submission.id,
        confirmed,
        googleReviewUrl || undefined,
        reviewerNote || undefined,
        referredCustomerName || undefined,
        referredCustomerPhone || undefined,
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Nộp bài thất bại');
    } finally {
      setBusy(false);
    }
  };

  const onCancel = () => {
    if (!token || !submission) return;
    setConfirmCancelVisible(true);
  };

  const confirmCancel = async () => {
    if (!token || !submission) return;
    setError(null);
    setBusy(true);
    try {
      await cancelSubmission(token, submission.id);
      router.back();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Huỷ nhận nhiệm vụ thất bại');
    } finally {
      setBusy(false);
      setConfirmCancelVisible(false);
    }
  };

  if (!submission) {
    if (loadError) {
      return (
        <ScreenContainer>
          <ErrorText>{loadError}</ErrorText>
          <Button title="Thử lại" onPress={load} />
        </ScreenContainer>
      );
    }
    return <LoadingState />;
  }

  if (showCamera) {
    return <CameraCapture onCapture={onPhotoTaken} onCancel={() => setShowCamera(false)} />;
  }

  const task = submission.task!;
  const shop = task.shop!;
  const photosNeeded = Math.max(0, task.requiredPhotos - submission.photos.length);
  const isGoogleReviewTask =
    task.taskType === 'GOOGLE_REVIEW_WITH_PURCHASE' || task.taskType === 'GOOGLE_REVIEW_NO_PURCHASE';
  const isReferralTask = task.taskType === 'CUSTOMER_REFERRAL';
  const isMysteryShopper = task.taskType === 'MYSTERY_SHOPPER';

  const onRaiseDispute = async () => {
    if (!token || !submission || disputeReason.trim().length < 10) return;
    setDisputing(true);
    setError(null);
    try {
      await disputeSubmission(token, submission.id, disputeReason.trim());
      setShowDisputeForm(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Gửi khiếu nại thất bại');
    } finally {
      setDisputing(false);
    }
  };

  return (
    <ScreenContainer>
      <ScrollView>
        <Card>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 16, fontFamily: fonts.bold, color: colors.text, flex: 1 }}>
              {task.title}
            </Text>
            <StatusBadge status={submission.status} />
          </View>
          <Text style={{ color: colors.textMuted, marginTop: 4 }}>{shop.name}</Text>
          <Text style={{ color: colors.textMuted }}>{shop.address}</Text>
          {isReferralTask && task.referralPercentTerms ? (
            <View style={{ marginTop: 10, padding: 10, borderRadius: 8, backgroundColor: '#FFFBEB' }}>
              <Text style={{ fontFamily: fonts.bold, color: colors.text, fontSize: 13 }}>
                Thoả thuận % đơn hàng (thoả thuận riêng với chủ shop)
              </Text>
              <Text style={{ color: colors.text, marginTop: 4 }}>{task.referralPercentTerms}</Text>
              <Text style={{ color: colors.textMuted, fontSize: 12, marginTop: 4 }}>
                Đây là thoả thuận riêng, ngoài số xu thưởng cố định. Loot không tính, không giữ và không xử
                lý tranh chấp phát sinh từ thoả thuận này.
              </Text>
            </View>
          ) : null}
        </Card>

        <ErrorText>{error}</ErrorText>

        {submission.status === 'ACCEPTED' || submission.status === 'CHECKED_IN' ? (
          <Button title="Huỷ nhận nhiệm vụ" variant="outline" onPress={onCancel} loading={busy} />
        ) : null}

        {submission.status === 'ACCEPTED' ? (
          <Card>
            <Text style={{ marginBottom: 10 }}>
              Bước 1: Đến địa điểm của shop, sau đó bấm check-in để xác nhận bạn đang có mặt trong bán
              kính {task.checkinRadiusMeters}m.
            </Text>
            <Button
              title="Xem đường đi"
              variant="outline"
              onPress={() => openDirections(shop.latitude, shop.longitude)}
            />
            <Button title="Check-in tại đây" onPress={onCheckin} loading={busy} />
          </Card>
        ) : null}

        {submission.status === 'CHECKED_IN' ? (
          <>
            <Card>
              <Text style={{ marginBottom: 10 }}>
                Bước 2: Chụp {task.requiredPhotos} ảnh theo hướng dẫn ({submission.photos.length}/
                {task.requiredPhotos} đã chụp).
              </Text>
              <Text style={{ color: colors.textMuted, marginBottom: 10, fontSize: 12 }}>
                {task.instructions}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                {submission.photos.map((p) =>
                  Platform.OS === 'web' ? (
                    <Pressable
                      key={p.id}
                      onPress={() => saveImageOnWeb(resolvePhotoUrl(p.url), `${p.id}.jpg`)}
                    >
                      <Image
                        source={{ uri: resolvePhotoUrl(p.url) }}
                        style={{ width: 72, height: 72, borderRadius: 8 }}
                      />
                    </Pressable>
                  ) : (
                    <Image
                      key={p.id}
                      source={{ uri: resolvePhotoUrl(p.url) }}
                      style={{ width: 72, height: 72, borderRadius: 8 }}
                    />
                  ),
                )}
              </View>
              {Platform.OS === 'web' && submission.photos.length > 0 ? (
                <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 10 }}>
                  Trên trình duyệt, ảnh KHÔNG tự lưu vào máy được (giới hạn của trình duyệt, không phải lỗi
                  app) — chạm vào từng ảnh ở trên, chọn "Lưu ảnh"/"Save Image" trong bảng hiện ra để lưu về máy.
                </Text>
              ) : null}
              <Button
                title={photosNeeded > 0 ? `Chụp ảnh (còn thiếu ${photosNeeded})` : 'Chụp thêm ảnh'}
                onPress={() => setShowCamera(true)}
                loading={busy}
              />
            </Card>

            {photosNeeded === 0 && isGoogleReviewTask ? (
              <Card>
                <Text style={{ marginBottom: 10, fontFamily: fonts.semiBold }}>
                  Bước 3: Đăng đánh giá thật lên Google Maps
                </Text>
                <Text style={{ marginBottom: 10 }}>
                  {Platform.OS === 'web'
                    ? 'Chạm vào từng ảnh ở Bước 2 để lưu về máy (xem hướng dẫn phía trên), sau đó dùng chính những ảnh đó'
                    : 'Ảnh bạn vừa chụp ở Bước 2 đã được lưu vào điện thoại — hãy dùng chính những ảnh đó'}{' '}
                  khi đăng đánh giá thật lên Google Maps cho shop này (không cần chụp màn hình). Chủ shop sẽ
                  đối chiếu ảnh bạn nộp với ảnh trong bài đánh giá trên Google Maps trước khi duyệt và trả
                  thưởng.
                </Text>

                {shop.googleReviewLink ? (
                  <>
                    <Button
                      title="✏️ Viết đánh giá ngay (mở thẳng ô đánh giá)"
                      onPress={() => openExternalUrl(shop.googleReviewLink!)}
                    />
                    <Text style={{ color: colors.textMuted, fontSize: 12, marginBottom: 14 }}>
                      Chấm 5 sao và viết nội dung, kèm đúng ảnh đã chụp ở Bước 2. Đăng xong, quay lại đây để
                      làm tiếp.
                    </Text>
                  </>
                ) : shop.googleMapsUrl ? (
                  <>
                    <Button
                      title="📍 Mở Google Maps để viết đánh giá"
                      onPress={() => openExternalUrl(shop.googleMapsUrl!)}
                    />
                    <View
                      style={{
                        backgroundColor: colors.primarySoft,
                        borderRadius: 10,
                        padding: 10,
                        marginBottom: 14,
                      }}
                    >
                      <Text style={{ color: colors.text, fontSize: 12.5, lineHeight: 18 }}>
                        Sau khi Maps mở ra, tại trang của shop:{'\n'}
                        1. Cuộn xuống mục "Bài đánh giá" (Reviews){'\n'}
                        2. Chạm biểu tượng cây bút ✏️ (hoặc nút "Viết đánh giá"){'\n'}
                        3. Chấm 5 sao và viết nội dung, kèm đúng ảnh đã chụp ở Bước 2{'\n'}
                        4. Đăng xong, quay lại đây (bấm mũi tên "Quay lại" hoặc chuyển app) để làm tiếp
                      </Text>
                    </View>
                  </>
                ) : (
                  <View style={{ backgroundColor: '#FFFBEB', borderRadius: 10, padding: 10, marginBottom: 14 }}>
                    <Text style={{ color: colors.text, fontSize: 12.5, lineHeight: 18 }}>
                      Chủ shop chưa gắn link Google Maps cho shop này. Hãy tự tìm đúng shop "{shop.name}" (
                      {shop.address}) trên Google Maps để đăng đánh giá.
                    </Text>
                  </View>
                )}

                <Text style={{ marginBottom: 6, fontFamily: fonts.semiBold }}>
                  Bước 4: Xác nhận và nộp bài
                </Text>
                <Text style={{ color: colors.textMuted, fontSize: 12.5, marginBottom: 8 }}>
                  Chỉ tick ô này SAU KHI đã đăng đánh giá thật xong ở Bước 3 — link/ghi chú bên dưới không
                  bắt buộc, nhưng ô tick này là bắt buộc mới nộp bài được.
                </Text>
                <Checkbox
                  checked={confirmedReviewPosted}
                  onToggle={() => setConfirmedReviewPosted((v) => !v)}
                  label="Tôi xác nhận đã đăng đánh giá trung thực lên Google Maps, sử dụng đúng ảnh đã chụp ở Bước 2"
                />

                <Label>Đường dẫn bài đánh giá Google Maps (không bắt buộc)</Label>
                <FormInput
                  value={googleReviewUrl}
                  onChangeText={setGoogleReviewUrl}
                  placeholder="https://maps.app.goo.gl/..."
                  autoCapitalize="none"
                />
                <Label>Ghi chú (không bắt buộc)</Label>
                <FormInput value={reviewerNote} onChangeText={setReviewerNote} placeholder="Nhập ghi chú nếu cần" />
                <Button
                  title="Nộp bài"
                  onPress={() => onSubmit(confirmedReviewPosted)}
                  loading={busy}
                  disabled={!confirmedReviewPosted}
                />
              </Card>
            ) : null}

            {photosNeeded === 0 && isReferralTask ? (
              <Card>
                <Text style={{ marginBottom: 10 }}>
                  Nhập tên và số điện thoại của khách bạn đã giới thiệu đến {shop.name} để chủ shop đối
                  chiếu trước khi duyệt.
                </Text>
                <Label>Tên khách</Label>
                <FormInput
                  value={referredCustomerName}
                  onChangeText={setReferredCustomerName}
                  placeholder="Nguyễn Văn A"
                />
                <Label>Số điện thoại khách</Label>
                <FormInput
                  value={referredCustomerPhone}
                  onChangeText={setReferredCustomerPhone}
                  keyboardType="phone-pad"
                  placeholder="09xxxxxxxx"
                />
                <Label>Ghi chú (không bắt buộc)</Label>
                <FormInput value={reviewerNote} onChangeText={setReviewerNote} placeholder="Nhập ghi chú nếu cần" />
                <Button
                  title="Nộp bài"
                  onPress={() => onSubmit(true)}
                  loading={busy}
                  disabled={!referredCustomerName.trim() || !referredCustomerPhone.trim()}
                />
              </Card>
            ) : null}

            {photosNeeded === 0 && isMysteryShopper ? (
              <Card>
                <Text style={{ marginBottom: 10 }}>
                  Bước 3: Viết nhận xét theo đúng tiêu chí chủ shop đã nêu trong hướng dẫn ở trên. Nhận
                  xét này chỉ gửi riêng cho chủ shop, không đăng công khai.
                </Text>
                <Label>Nhận xét đánh giá</Label>
                <FormInput
                  value={reviewerNote}
                  onChangeText={setReviewerNote}
                  placeholder="Nhận xét chi tiết theo từng tiêu chí"
                  multiline
                />
                <Button
                  title="Nộp bài"
                  onPress={() => onSubmit(true)}
                  loading={busy}
                  disabled={reviewerNote.trim().length < 10}
                />
              </Card>
            ) : null}

            {photosNeeded === 0 && !isGoogleReviewTask && !isReferralTask && !isMysteryShopper ? (
              <Card>
                <Text style={{ marginBottom: 10 }}>
                  Bước 3: Kiểm tra lại ảnh minh chứng đã chụp đủ, thêm ghi chú nếu cần rồi nộp bài. Xu
                  thưởng sẽ được cộng khi bài nộp được duyệt.
                </Text>
                <Label>Ghi chú (không bắt buộc)</Label>
                <FormInput value={reviewerNote} onChangeText={setReviewerNote} placeholder="Nhập ghi chú nếu cần" />
                <Button title="Nộp bài" onPress={() => onSubmit(true)} loading={busy} />
              </Card>
            ) : null}
          </>
        ) : null}

        {submission.status === 'SUBMITTED' ? (
          <Card>
            <Text>Bài nộp của bạn đang chờ chủ shop duyệt. Xu thưởng sẽ được cộng ngay khi được duyệt.</Text>
          </Card>
        ) : null}

        {submission.status === 'APPROVED' ? (
          <>
            <Card>
              <Text style={{ color: colors.success, fontFamily: fonts.bold }}>
                Bài nộp đã được duyệt! Bạn nhận được {task.rewardCoins} xu.
              </Text>
            </Card>
            {token ? (
              <ShopRatingForm token={token} shopId={shop.id} submissionId={submission.id} />
            ) : null}
          </>
        ) : null}

        {submission.status === 'REJECTED' ? (
          <>
            <Card>
              <Text style={{ color: colors.danger, fontFamily: fonts.bold }}>Bài nộp đã bị từ chối</Text>
              {submission.rejectReason ? (
                <Text style={{ color: colors.textMuted, marginTop: 4 }}>
                  Lý do: {submission.rejectReason}
                </Text>
              ) : null}
              <Button title="Quay lại" variant="outline" onPress={() => router.back()} />
            </Card>

            {submission.disputeStatus === 'OPEN' ? (
              <Card>
                <Text style={{ color: colors.warning, fontFamily: fonts.bold }}>
                  Khiếu nại đang chờ admin xử lý
                </Text>
                <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 13 }}>
                  {submission.disputeReason}
                </Text>
              </Card>
            ) : submission.disputeStatus === 'RESOLVED_REVIEWER' ? (
              <Card>
                <Text style={{ color: colors.success, fontFamily: fonts.bold }}>
                  Khiếu nại của bạn đã được chấp nhận, xu thưởng đã được hoàn vào ví.
                </Text>
              </Card>
            ) : submission.disputeStatus === 'RESOLVED_OWNER' ? (
              <Card>
                <Text style={{ color: colors.textMuted, fontFamily: fonts.bold }}>
                  Admin đã xem xét khiếu nại và giữ nguyên quyết định từ chối.
                </Text>
                {submission.disputeAdminNote ? (
                  <Text style={{ color: colors.textMuted, marginTop: 4, fontSize: 13 }}>
                    Ghi chú của admin: {submission.disputeAdminNote}
                  </Text>
                ) : null}
              </Card>
            ) : showDisputeForm ? (
              <Card>
                <Label>Vì sao bạn cho rằng quyết định từ chối này không thoả đáng?</Label>
                <FormInput
                  value={disputeReason}
                  onChangeText={setDisputeReason}
                  placeholder="Mô tả chi tiết, admin sẽ xem lại ảnh và thông tin check-in"
                  multiline
                />
                <Caption style={{ marginTop: -6, marginBottom: 10 }}>
                  Tối thiểu 10 ký tự ({disputeReason.trim().length}/10)
                </Caption>
                <Button
                  title="Gửi khiếu nại"
                  onPress={onRaiseDispute}
                  loading={disputing}
                  disabled={disputeReason.trim().length < 10}
                />
              </Card>
            ) : (
              <Pressable onPress={() => setShowDisputeForm(true)} style={{ alignItems: 'center', marginBottom: 12 }}>
                <Text style={{ color: colors.primary, fontSize: 13, textDecorationLine: 'underline' }}>
                  Tôi cho rằng quyết định từ chối này không thoả đáng — gửi khiếu nại
                </Text>
              </Pressable>
            )}

            {token ? (
              <ShopRatingForm token={token} shopId={shop.id} submissionId={submission.id} />
            ) : null}
          </>
        ) : null}
      </ScrollView>
      <ConfirmDialog
        visible={confirmCancelVisible}
        title="Huỷ nhận nhiệm vụ này?"
        message="Bạn sẽ không nhận được xu thưởng cho nhiệm vụ này."
        confirmLabel="Huỷ nhận"
        danger
        loading={busy}
        onConfirm={confirmCancel}
        onCancel={() => setConfirmCancelVisible(false)}
      />
    </ScreenContainer>
  );
}
